import { z } from "zod";

/**
 * Animation overlay sizing modes
 */
export const overlaySizeModeSchema = z
  .enum([
    "auto", // Use Lottie intrinsic dimensions (default)
    "contain", // Fit within container, preserve aspect ratio
    "cover", // Fill container, may crop, preserve aspect ratio
    "fill", // Stretch to fill (ignores aspect ratio)
  ])
  .describe(
    'How the overlay is sized: "auto" uses the Lottie file\'s intrinsic dimensions (default), "contain" fits within the container preserving aspect ratio, "cover" fills the container (may crop) preserving aspect ratio, "fill" stretches to fill ignoring aspect ratio.',
  );

/**
 * CSS inset value used by overlay position fields: a string with units
 * ("10%", "50px") or a bare number of pixels (50).
 */
const insetValueSchema = z
  .union([z.string(), z.number()])
  .describe(
    'CSS inset value: a string with units (e.g. "10%" of container, "50px") or a bare number of pixels (e.g. 50).',
  );

/**
 * Animation overlay configuration (Lottie, video, gif, etc.)
 */
export const overlayConfigSchema = z
  .object({
    src: z
      .string()
      .describe(
        'Path to the animation file, e.g. "/dots-dancing.json" for a Lottie JSON served from public/.',
      ),
    type: z
      .literal("lottie")
      .optional()
      .describe(
        'Overlay media type. Only "lottie" is supported today (the default); "video" and "gif" are reserved for the future.',
      ),

    // Positioning (default: centered)
    position: z
      .object({
        top: insetValueSchema.optional(),
        bottom: insetValueSchema.optional(),
        left: insetValueSchema.optional(),
        right: insetValueSchema.optional(),
      })
      .optional()
      .describe(
        'Absolute positioning within the container. Each edge accepts a CSS string with units ("10%", "50px") or a bare pixel number (50). Omit to center the overlay.',
      ),

    // Sizing
    sizeMode: overlaySizeModeSchema
      .optional()
      .describe(
        'How to calculate the overlay size (default: "auto" = Lottie intrinsic dimensions).',
      ),
    sizePercent: z
      .union([
        z
          .number()
          .describe(
            "Number shorthand: one value applied to both width and height (e.g. 25 = 25% of container).",
          ),
        z.object({
          width: z
            .number()
            .optional()
            .describe(
              "Width as a percentage of the container (e.g. 25 = 25%).",
            ),
          height: z
            .number()
            .optional()
            .describe(
              "Height as a percentage of the container (e.g. 25 = 25%).",
            ),
        }),
      ])
      .optional()
      .describe(
        "Container-relative sizing. A bare number sizes both dimensions (25 = 25% width and height); the object form sets width/height independently.",
      ),
    maxSize: z
      .object({
        width: z.number().optional().describe("Maximum width in pixels."),
        height: z.number().optional().describe("Maximum height in pixels."),
      })
      .optional()
      .describe("Maximum size constraints in pixels."),
    minSize: z
      .object({
        width: z.number().optional().describe("Minimum width in pixels."),
        height: z.number().optional().describe("Minimum height in pixels."),
      })
      .optional()
      .describe("Minimum size constraints in pixels."),
    aspectRatio: z
      .number()
      .optional()
      .describe(
        "Manual aspect ratio override as width/height (e.g. 1.5 for a 3:2 overlay).",
      ),

    // Rendering scope
    relativeTo: z
      .enum(["container", "viewport"])
      .optional()
      .describe(
        'What the overlay is positioned and sized against: "container" (default) renders inside the item/group/section box; "viewport" uses a portal for full-screen rendering.',
      ),

    // Post-scaling adjustments
    offset: z
      .object({
        x: insetValueSchema
          .optional()
          .describe(
            'Horizontal offset: "10%" (of container), "50px", or a bare pixel number (50).',
          ),
        y: insetValueSchema
          .optional()
          .describe(
            'Vertical offset: "10%" (of container), "50px", or a bare pixel number (50).',
          ),
      })
      .optional()
      .describe("Position offset applied after sizing/positioning."),
    rotationOffset: z
      .number()
      .optional()
      .describe("Additional rotation in degrees applied after positioning."),

    // Base properties
    rotation: z.number().optional().describe("Initial rotation in degrees."),
    opacity: z
      .number()
      .min(0)
      .max(1)
      .optional()
      .describe("Overlay opacity from 0 to 1 (default: 1)."),
    loop: z
      .union([z.boolean(), z.number().int().min(1)])
      .optional()
      .describe(
        "Playback looping: true = loop forever (default), false = play once and unmount, a number = play N times and unmount.",
      ),
    persist: z
      .boolean()
      .optional()
      .describe(
        "Keep the last frame visible after the animation ends instead of unmounting (default: false).",
      ),
    speed: z
      .number()
      .optional()
      .describe(
        "Playback speed multiplier (default: 1; e.g. 0.5 = half speed, 2 = double speed).",
      ),
    zIndex: z.number().optional().describe("CSS stack order (default: 10)."),
    allowOverflow: z
      .boolean()
      .optional()
      .describe(
        "Allow the overlay to extend beyond the container bounds (default: true).",
      ),
    color: z
      .string()
      .optional()
      .describe(
        'Hex color that overrides every SVG fill/stroke in the Lottie (e.g. "#FF0000").',
      ),
    delay: z
      .number()
      .optional()
      .describe(
        "Delay in milliseconds before rendering the animation (default: 0).",
      ),
  })
  .describe(
    "Animation overlay configuration (Lottie). Overlays can attach to an item, group, or section and are positioned/sized relative to their container or the viewport.",
  );

/**
 * Ken Burns effect configuration for background images.
 * Controls a slow pan-and-zoom animation over a still photograph.
 *
 * The effect works by animating from one "view rectangle" to another:
 *   startPosition → endPosition   (pan)
 *   startScale    → endScale      (zoom)
 *
 * Position values use a 0-100 coordinate system representing the
 * focal point on the image (50/50 = center, 0/0 = top-left).
 */
export const kenBurnsConfigSchema = z
  .object({
    // --- Motion endpoints ---
    startPosition: z
      .object({
        x: z
          .number()
          .min(0)
          .max(100)
          .optional()
          .describe("Horizontal focal point at start, 0-100 (default: 30)."),
        y: z
          .number()
          .min(0)
          .max(100)
          .optional()
          .describe("Vertical focal point at start, 0-100 (default: 30)."),
      })
      .optional()
      .describe(
        "Focal point where the camera starts, on a 0-100 grid (50/50 = image center; default: 30/30).",
      ),
    endPosition: z
      .object({
        x: z
          .number()
          .min(0)
          .max(100)
          .optional()
          .describe("Horizontal focal point at end, 0-100 (default: 70)."),
        y: z
          .number()
          .min(0)
          .max(100)
          .optional()
          .describe("Vertical focal point at end, 0-100 (default: 70)."),
      })
      .optional()
      .describe(
        "Focal point where the camera ends, on a 0-100 grid (default: 70/70).",
      ),
    startScale: z
      .number()
      .min(1)
      .max(3)
      .optional()
      .describe("Zoom level at start, 1-3 (default: 1 = no zoom)."),
    endScale: z
      .number()
      .min(1)
      .max(3)
      .optional()
      .describe("Zoom level at end, 1-3 (default: 1.15)."),

    // --- Timing ---
    duration: z
      .number()
      .min(1)
      .optional()
      .describe("Full pan/zoom cycle duration in seconds (default: 6)."),
    easing: z
      .union([z.string(), z.array(z.number()).length(4)])
      .optional()
      .describe(
        'Easing curve (default: [0.5, 0, 0.5, 1] — slow start, fast middle, slow finish). Accepts a Framer Motion named easing string ("linear", "easeIn", "easeOut", "easeInOut", "circIn", "circOut", "circInOut", "backIn", "backOut", "backInOut", "anticipate") or a custom cubic-bezier as a 4-number array [x1, y1, x2, y2].',
      ),

    // --- Looping ---
    loop: z
      .boolean()
      .optional()
      .describe("Reverse direction and repeat continuously (default: false)."),
  })
  .describe(
    "Ken Burns pan-and-zoom configuration for background images: the camera animates from startPosition/startScale to endPosition/endScale over duration seconds. Positions use a 0-100 focal-point grid (50/50 = center).",
  );

/**
 * Background media configuration for sections
 */
export const backgroundMediaConfigSchema = z
  .object({
    src: z.string().describe("Path to the image or video file."),
    persist: z
      .boolean()
      .optional()
      .describe(
        "For videos: freeze on the last frame instead of looping (default: false).",
      ),
    opacity: z
      .number()
      .min(0)
      .max(1)
      .optional()
      .describe("Opacity of the media, 0-1 (default: 1)."),
    kenBurns: z
      .union([z.boolean(), kenBurnsConfigSchema])
      .optional()
      .describe(
        "Ken Burns pan-and-zoom effect (images only). On by default; set false to disable, true for defaults, or an object to customize.",
      ),
  })
  .describe(
    "Background media config object. Use this form instead of a plain src string when you need persist, opacity, or Ken Burns control.",
  );

/**
 * Color palette for gradient backgrounds
 */
export const colorPaletteSchema = z
  .object({
    from: z
      .string()
      .describe('Gradient start color as a hex string (e.g. "#0e3f05").'),
    via: z
      .string()
      .optional()
      .describe("Optional gradient middle color as a hex string."),
    to: z.string().describe("Gradient end color as a hex string."),
    degrees: z
      .number()
      .optional()
      .describe("Gradient angle in degrees (default: 180 = top to bottom)."),
  })
  .describe("Color palette for a section's gradient background.");

/**
 * Union type for single or multiple overlays
 * Supports both legacy single overlay and new stacking overlay feature
 */
export const overlayOrArraySchema = z
  .union([overlayConfigSchema, z.array(overlayConfigSchema)])
  .describe(
    "A single overlay config, or an array of overlay configs stacked in order.",
  );

// Export inferred types
export type OverlaySizeMode = z.infer<typeof overlaySizeModeSchema>;
export type OverlayConfig = z.infer<typeof overlayConfigSchema>;
export type KenBurnsConfig = z.infer<typeof kenBurnsConfigSchema>;
export type BackgroundMediaConfig = z.infer<typeof backgroundMediaConfigSchema>;
export type ColorPalette = z.infer<typeof colorPaletteSchema>;
